import React, { useState } from 'react';
import { clsx } from 'clsx';
import { NavLink } from 'react-router-dom';
import { Icon } from '@/design-system/assets/icons/Icon.jsx';

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
  badge?: string | number;
  children?: SidebarItem[];
}

interface SidebarSection {
  id: string;
  title: string;
  items: SidebarItem[];
}

interface SidebarProps {
  sections: SidebarSection[];
  className?: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  sections,
  className,
  collapsed = false,
  onToggleCollapse,
  mobileOpen = false,
  onMobileClose,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const renderSidebarItem = (item: SidebarItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);

    return (
      <div key={item.id}>
        {hasChildren ? (
          <button
            onClick={() => toggleExpanded(item.id)}
            className={clsx(
              'w-full flex items-center justify-between',
              'px-3.5 py-2.5 mx-0.5 rounded-xl text-sm',
              'text-foreground/80 hover:text-foreground',
              'hover:bg-secondary/70 hover:border-border/60',
              'border border-transparent',
              'transition-all duration-150 ease-out',
              'group',
              level > 0 && 'ml-3'
            )}
          >
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 flex-shrink-0 text-muted-foreground group-hover:text-primary transition-colors">
                {item.icon}
              </span>
              {!collapsed && (
                <>
                  <span className="font-medium truncate">{item.label}</span>
                  {item.badge && (
                    <span className="px-2 py-0.5 text-[10px] bg-primary/15 text-primary border border-primary/25 rounded-full font-bold">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </div>
            {!collapsed && hasChildren && (
              <Icon
                name="ChevronForward"
                size={14}
                className={clsx(
                  'transition-transform duration-200 text-muted-foreground',
                  isExpanded && 'rotate-90'
                )}
              />
            )}
          </button>
        ) : (
          <NavLink
            to={item.href}
            onClick={onMobileClose}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3',
                'px-3.5 py-2.5 mx-0.5 rounded-xl text-sm',
                'transition-all duration-150 ease-out',
                'group border',
                level > 0 && 'ml-3',
                isActive
                  ? 'bg-gradient-to-r from-indigo-500/15 to-violet-500/10 text-primary font-semibold border-indigo-500/30 shadow-xs'
                  : 'text-foreground/80 hover:text-foreground hover:bg-secondary/70 border-transparent hover:border-border/60'
              )
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={clsx(
                    'w-5 h-5 flex-shrink-0 transition-colors',
                    isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'
                  )}
                >
                  {item.icon}
                </span>
                {!collapsed && (
                  <>
                    <span className="truncate">{item.label}</span>
                    {item.badge && (
                      <span className="px-2 py-0.5 text-[10px] bg-primary/15 text-primary border border-primary/25 rounded-full ml-auto font-bold">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </NavLink>
        )}

        {/* Render children */}
        {hasChildren && isExpanded && !collapsed && (
          <div className="ml-3 pl-3 border-l border-border/70 my-1 space-y-0.5">
            {item.children?.map((child) => renderSidebarItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderSection = (section: SidebarSection) => (
    <div key={section.id} className="mb-5">
      {!collapsed && (
        <div className="px-3.5 mb-1.5">
          <h3 className="text-[11px] font-bold tracking-wider text-muted-foreground uppercase">
            {section.title}
          </h3>
        </div>
      )}
      <div className="space-y-1">
        {section.items.map((item) => renderSidebarItem(item))}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar Container */}
      <aside
        className={clsx(
          'fixed lg:sticky top-14 lg:top-16 right-0 lg:right-auto lg:left-0 h-[calc(100vh-3.5rem)] lg:h-[calc(100vh-4rem)]',
          'bg-sidebar/90 backdrop-blur-2xl border-l lg:border-l-0 lg:border-r border-sidebar-border/80',
          'transition-all duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
          'z-50 lg:z-0',
          'overflow-y-auto no-scrollbar',
          collapsed ? 'w-[64px] lg:w-[64px]' : 'w-[240px] lg:w-[240px]',
          !mobileOpen && 'translate-x-full lg:translate-x-0',
          mobileOpen && 'translate-x-0',
          className
        )}
      >

        {/* Collapse Toggle - Only visible on desktop if needed, or moved to bottom */}
        <div className="hidden lg:flex items-center justify-between p-3 mb-2">
          {!collapsed && (
            <div className="px-2 py-1">
              {/* Optional Brand or Context Switcher Area */}
              <span className="text-xs font-medium text-sidebar-foreground">Main Workspace</span>
            </div>
          )}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className={clsx(
                'p-1.5 rounded-full ml-auto',
                'text-sidebar-foreground/50 hover:text-sidebar-foreground',
                'hover:bg-sidebar-accent/50',
                'transition-colors duration-150',
                collapsed && 'mx-auto'
              )}
            >
              <svg
                className={clsx(
                  'w-4 h-4 transition-transform duration-300',
                  collapsed && 'rotate-180'
                )}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>

        {/* Navigation Items */}
        <nav className="p-2 space-y-6">
          {sections.map((section) => renderSection(section))}
        </nav>
      </aside>
    </>
  );
};